import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  LayoutDashboard,
  Users,
  Shield,
  Briefcase,
  TrendingUp,
  FileText,
  LogOut,
  Menu,
  X,
  ChevronRight,
  Bell,
  AlertTriangle,
  MessageSquare,
  Target,
  Lock
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Admin Notification Bar Component
const AdminNotificationBar = ({ user }) => {
  const [notifications, setNotifications] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchNotifications();
    }
  }, [user]);

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const [usersRes, reportsRes] = await Promise.all([
        axios.get(`${API}/admin/all-users`, config),
        axios.get(`${API}/admin/all-reports`, config)
      ]);

      const allUsers = usersRes.data;
      const allReports = reportsRes.data;

      const notifs = [];

      // Check for unassigned users
      const unassignedUsers = allUsers.filter(u => u.role === 'user' && !u.rm_id);
      if (unassignedUsers.length > 0) {
        notifs.push({
          type: 'unassigned',
          count: unassignedUsers.length,
          message: `${unassignedUsers.length} user${unassignedUsers.length > 1 ? 's' : ''} not assigned to RM`,
          action: () => navigate('/admin/clients')
        });
      }

      // Check for new reports
      const newReports = allReports.filter(r => r.status === 'draft' || r.status === 'sent');
      if (newReports.length > 0) {
        notifs.push({
          type: 'reports',
          count: newReports.length,
          message: `${newReports.length} new report${newReports.length > 1 ? 's' : ''} generated`,
          action: () => navigate('/admin/reviews')
        });
      }

      setNotifications(notifs);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  return (
    <div className="sticky top-0 z-20 bg-white border-b border-gray-200 px-6 py-3">
      <div className="flex items-center justify-end">
        <div className="relative">
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            className="relative p-2 hover:bg-[#fffdf0] rounded-lg transition-colors"
          >
            <Bell size={20} className="text-ugmonk-dark" />
            {notifications.length > 0 && (
              <span className="absolute top-1 right-1 w-2 h-2 bg-ugmonk-yellow rounded-full animate-pulse"></span>
            )}
          </button>

          {showDropdown && (
            <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-xl shadow-black/5 border border-gray-100 py-2 animate-in fade-in slide-in-from-top-2 duration-200">
              <div className="px-4 py-2 border-b border-gray-100">
                <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
              </div>
              {notifications.length === 0 ? (
                <div className="px-4 py-6 text-center text-sm text-gray-500">
                  No new notifications
                </div>
              ) : (
                <div className="max-h-96 overflow-y-auto">
                  {notifications.map((notif, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        notif.action();
                        setShowDropdown(false);
                      }}
                      className="w-full px-4 py-3 hover:bg-[#fffdf0] transition-colors text-left"
                    >
                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-lg bg-[#fffdf0]">
                          {notif.type === 'unassigned' ? (
                            <Users size={16} className="text-ugmonk-dark" />
                          ) : (
                            <FileText size={16} className="text-ugmonk-dark" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-900 font-medium">{notif.message}</p>
                          <p className="text-xs text-gray-500 mt-0.5">Click to view</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Layout = ({ children, user, onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    onLogout();
    navigate('/');
  };

  // Admin navigation - Dashboard, Family Management, Enhanced Reports, Clients, Reviews
  const adminNavItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/security-master', label: 'Security Master', icon: Briefcase },
    { path: '/admin/market-outlook', label: 'Market Outlook', icon: TrendingUp },
    { path: '/admin/recommendations', label: 'Recommendations', icon: Target },
    { path: '/deviation-handling', label: 'Deviation Handling', icon: AlertTriangle },
    { path: '/admin/clients-prospects', label: 'Clients & Prospects', icon: Users },
    { path: '/admin/reviews', label: 'RM Reports', icon: FileText },
  ];

  // RM navigation - all features (Portfolios moved to Prospects View Details)
  const rmNavItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/market-outlook', label: 'Market Outlook', icon: TrendingUp },
    { path: '/clients-prospects', label: 'Clients & Prospects', icon: Users },
    { path: '/security-master', label: 'Security Master', icon: Briefcase },
    { path: '/deviation-handling', label: 'Deviation Handling', icon: AlertTriangle },
    { path: '/rm-knowledge-centre', label: 'RM Knowledge Centre', icon: FileText, featureKey: 'knowledge_centre' },
    { path: '/chat', label: 'OlafAI', icon: MessageSquare },
    { path: '/workflow', label: 'Generate Report', icon: FileText, featureKey: 'generate_report' },
  ];

  // User navigation - Phase 1 (Risk profiling only)
  const getUserName = () => {
    if (!user?.full_name) return 'User';
    const firstName = user.full_name.split(' ')[0];
    return firstName;
  };

  const userNavItems = [
    { path: '/dashboard', label: `Welcome ${getUserName()}`, icon: LayoutDashboard },
    { path: '/risk-profile-results', label: 'Risk Profile', icon: Shield },
  ];

  // Select navigation based on role
  const navItems = user?.role === 'admin' ? adminNavItems : user?.role === 'rm' ? rmNavItems : userNavItems;

  const isAdmin = user?.role === 'admin';

  return (
    <div className="flex h-screen bg-[#f8f6f1]">
      {/* Mobile Menu Button */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-lg bg-[#dae4c4] border border-[#c5d6af] text-[#404040] shadow-sm"
        data-testid="mobile-menu-button"
      >
        {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      {/* Sidebar */}
      <aside
        className={`${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 fixed lg:static inset-y-0 left-0 z-40 w-64 border-r transition-transform duration-300 bg-[#dae4c4] border-[#c5d6af]`}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-center" style={{ height: '64px', borderBottom: '1px solid #c5d6af', overflow: 'hidden' }}>
            <img
              src="/vaia-icon.png"
              alt="VAIA"
              style={{ height: '40px', width: 'auto', objectFit: 'contain' }}
              onError={(e) => { e.target.src = '/vaia-logo-white.png'; }}
            />
          </div>

          {/* Navigation */}
          <nav className="flex-1 py-2 space-y-0.5 overflow-y-auto">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              const isDisabled = item.featureKey && user?.disabled_features?.includes(item.featureKey);

              if (isDisabled) {
                return (
                  <div
                    key={item.path}
                    className="flex items-center gap-3 pl-4 pr-4 py-2.5 cursor-not-allowed opacity-40 text-[#404040] border-l-4 border-transparent"
                    data-testid={`nav-${item.path.slice(1)}`}
                  >
                    <Icon size={18} className="shrink-0" />
                    <span className="flex-1 text-[13px]">{item.label}</span>
                    <Lock size={14} />
                  </div>
                );
              }

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`sidebar-item w-full flex items-center gap-3 py-2.5 text-sm transition-colors border-l-4 ${
                    isActive
                      ? 'bg-[#fcfcf5] text-[#1a1a1a] font-medium pl-3 pr-4'
                      : 'text-[#404040] hover:bg-[#c8d9b0] hover:text-[#1a1a1a] border-transparent pl-4 pr-4'
                  }`}
                  style={isActive ? { borderLeftColor: '#ffd879' } : {}}
                  data-testid={`nav-${item.path.slice(1)}`}
                >
                  <Icon size={18} className="shrink-0" />
                  <span className="flex-1 text-left">{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* User Profile & Logout */}
          <div className="mt-auto" style={{ borderTop: '1px solid #c5d6af' }}>
            <div className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-[#404040] text-sm font-semibold border border-[#c5d6af]">
                  {user?.full_name?.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate text-[#404040]">{user?.full_name}</p>
                  <span className="inline-block text-xs px-2 py-0.5 rounded-full font-medium text-[#555] border border-[#c5d6af] bg-white/50">
                    {user?.role === 'admin' ? 'Administrator' :
                     user?.role === 'rm' ? 'Relationship Manager' :
                     'Client'}
                  </span>
                </div>
              </div>
              <Button
                onClick={handleLogout}
                variant="ghost"
                className="w-full justify-start text-sm text-[#666] hover:text-[#1a1a1a] hover:bg-[#c8d9b0]"
                data-testid="logout-button"
              >
                <LogOut size={18} className="mr-3" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {/* Top Bar for Admin */}
        {user?.role === 'admin' && <AdminNotificationBar user={user} />}

        <div className="p-6 lg:p-8 max-w-[1720px] mx-auto page-enter">{children}</div>
      </main>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/30 backdrop-blur-sm z-30"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default Layout;
